/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useModelStore as useBotDetailModelStore } from '@adp-studio/bot-detail-store/model';
import { useBotInfoStore } from '@adp-studio/bot-detail-store/bot-info';
import { BotMode } from '@adp-arch/bot-api/developer_api';

import {
  defaultModelCapConfig,
  getBetterHarnessModelCapabilityConfig,
  getSingleAgentModelCapabilityConfig,
  type TGetModelCapabilityConfig,
} from '../../utils/model-capability';
import { useBotEditor } from '../../context/bot-editor-context';

const getModelCapabilityConfigMap: Record<BotMode, TGetModelCapabilityConfig> =
  {
    [BotMode.SingleMode]: getSingleAgentModelCapabilityConfig,
    [BotMode.ClientSideToolMode]: getSingleAgentModelCapabilityConfig,
    [BotMode.WorkflowMode]: () => defaultModelCapConfig,
    [BotMode.HarnessMode]: getBetterHarnessModelCapabilityConfig,
  };

export const useModelCapabilityConfig = () => {
  const {
    storeSet: { useModelStore },
  } = useBotEditor();
  const getModelById = useModelStore(store => store.getModelById);
  const mode = useBotInfoStore(store => store.mode);
  const modelIds = useGetModelIdsByMode(mode);
  return getModelCapabilityConfigMap[mode]({
    modelIds,
    getModelById,
  });
};

const useGetModelIdsByMode = (mode: BotMode) => {
  const singleModeId = useBotDetailModelStore(
    store => store.config.model ?? '',
  );
  const getModeIdsMap: Record<BotMode, string[]> = {
    [BotMode.SingleMode]: [singleModeId],
    [BotMode.ClientSideToolMode]: [singleModeId],
    [BotMode.HarnessMode]: [singleModeId],
    [BotMode.WorkflowMode]: [],
  };
  return getModeIdsMap[mode];
};
